import { HTMLAttributes, ReactNode } from "react";
import { messageIllustrations } from "../../../assets/illustrations";
import { cx } from "../../utils/cx";
import { Button } from "../Button";
import { Message } from "./Message";

export type Message404PageProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    "title"
> & {
    /**
     * Illustration URL. Defaults to bundled DS SVG; in the legacy shell pass the
     * public asset (`/illustrations/not-found-404.svg`) via `imageSrc` if needed.
     */
    imageSrc?: string;
    title?: ReactNode;
    description?: ReactNode;
    /** Primary button label (e.g. “Go home”). */
    homeLabel?: string;
    /** If omitted, the button calls `window.location.assign("/")`. */
    onGoHome?: () => void;
};

const defaultTitle = "404: Page not found";
const defaultDescription =
    "The page you are looking for does not exist or was moved. Check the URL or go back home.";
const defaultHomeLabel = "Go home";

/**
 * 404 state page based on the **Message** pattern (empty / error anatomy).
 * Use inside a layout (e.g. `Content`) or full screen.
 */
export function Message404Page({
    imageSrc = messageIllustrations.notFound404,
    title = defaultTitle,
    description = defaultDescription,
    homeLabel = defaultHomeLabel,
    onGoHome,
    className,
    ...rest
}: Message404PageProps) {
    const handleGoHome = () => {
        if (onGoHome) {
            onGoHome();
        } else {
            window.location.assign("/");
        }
    };

    const graphic = (
        <img
            alt="Page not found illustration"
            decoding="async"
            height={116}
            src={imageSrc}
            style={{
                display: "block",
                height: "auto",
                maxWidth: 320,
                width: "100%",
            }}
            width={163}
        />
    );

    return (
        <div className={cx(className)} data-ui="message-404-page" {...rest}>
            <Message
                actions={
                    <Button onClick={handleGoHome} size="md" type="button">
                        {homeLabel}
                    </Button>
                }
                description={description}
                graphic={graphic}
                title={title}
            />
        </div>
    );
}

Message404Page.displayName = "Message404Page";
